import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocFromServer,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  Unsubscribe,
  updateDoc,
  where,
} from 'firebase/firestore';
import { User as FirebaseUser } from 'firebase/auth';
import { db } from '../platform/firebase/clients';
import { ApprovedSubmissionLocation, Task, TaskStatus, TASK_STATUSES } from '../types';
import { LEGACY_TYPE_ID } from '../taskTypes/constants';
import { readCampaignClaimPolicy } from '../domain/taskPolicy';
import { wrapSubscriptionError } from './subscriptionError';
import { ValidatorComment } from './taskValidator';
import { claimScopeKey } from '../domain/taskQueue/claimScope';
import type { TaskClaimScope } from '../domain/taskQueue/claimScope';
import { compactTaskResult } from '../domain/honeydewResult';
import { createHoneydewTaskLoader, loadTaskFileContents } from './honeydewResultStorage';
import {
  invokeTaskCommand,
  isTaskCommandError,
  newTaskCommandIdempotencyKey,
  TaskCommandError,
} from './taskCommands';

const TASKS = 'tasks';

// Firestore caps documents at 1,048,576 bytes. Hold back ~148 KiB for the
// surrounding scalar fields (status, ownerName/Email, reviewer*, timestamps,
// activity counters) so a result that fits this gate still fits the whole
// doc. Caught here so the write never reaches Firestore — the user gets an
// actionable error instead of the raw FirebaseError.
const MAX_RESULT_PAYLOAD_BYTES = 900 * 1024;

export class ResultPayloadTooLargeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ResultPayloadTooLargeError';
  }
}

function assertResultFitsFirestore(result: Record<string, unknown>): void {
  if (!result || typeof result !== 'object' || Array.isArray(result)) {
    throw new Error('Task result must be a JSON object.');
  }
  const size = new TextEncoder().encode(JSON.stringify(result)).byteLength;
  if (size > MAX_RESULT_PAYLOAD_BYTES) {
    const sizeKb = (size / 1024).toFixed(0);
    const maxKb = (MAX_RESULT_PAYLOAD_BYTES / 1024).toFixed(0);
    throw new ResultPayloadTooLargeError(
      `This task's data is too large to save (${sizeKb} KB). Firestore documents are limited to ${maxKb} KB of result content. Please trim or split your work before saving.`,
    );
  }
}

function normalizeOptionalGpuMetadata(
  result: Record<string, unknown>,
): Record<string, unknown> {
  const metadata = result.metadata;
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return result;

  const metadataRecord = metadata as Record<string, unknown>;
  const envGpus = metadataRecord.envGpus;
  if (typeof envGpus !== 'number' || Number.isFinite(envGpus)) return result;

  // Older uploads represented an omitted optional GPU setting as NaN. Firestore
  // can retain that value, but callable commands use JSON and cannot encode it.
  return {
    ...result,
    metadata: {
      ...metadataRecord,
      envGpus: null,
    },
  };
}

// One definition, in the domain. Re-exported because callers import both
// from here today.
export { claimScopeKey };
export type { TaskClaimScope };

function toDate(value: unknown): Date {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return new Date(0);
}

function toDateOrNull(value: unknown): Date | null {
  if (value == null) return null;
  return toDate(value);
}

function mapTask(id: string, data: Record<string, any>): Task {
  return {
    id,
    campaignId: data.campaignId ?? '',
    campaignName: data.campaignName ?? '',
    title: data.title ?? '',
    description: data.description ?? '',
    payload: data.payload ?? {},
    taskType: data.taskType ?? LEGACY_TYPE_ID,
    ...(typeof data.uploadKey === 'string' && data.uploadKey
      ? { uploadKey: data.uploadKey }
      : {}),
    status: (data.status ?? 'available') as TaskStatus,
    priority: data.priority ?? 0,
    tags: data.tags ?? [],
    createdBy: data.createdBy ?? '',
    createdByName: data.createdByName ?? '',
    ownerId: data.ownerId ?? null,
    ownerName: data.ownerName ?? null,
    ownerEmail: typeof data.ownerEmail === 'string' ? data.ownerEmail : null,
    expertId: typeof data.expertId === 'string' ? data.expertId : null,
    reviewerId: data.reviewerId ?? null,
    reviewerName: data.reviewerName ?? null,
    escalatedAt: toDateOrNull(data.escalatedAt),
    escalatedReviewRound: Number.isInteger(data.escalatedReviewRound)
      ? data.escalatedReviewRound
      : null,
    escalatedById: typeof data.escalatedById === 'string' ? data.escalatedById : null,
    escalatedByName: typeof data.escalatedByName === 'string' ? data.escalatedByName : null,
    escalatedByEmail: typeof data.escalatedByEmail === 'string' ? data.escalatedByEmail : null,
    escalationNote: typeof data.escalationNote === 'string' ? data.escalationNote : null,
    result: data.result ?? null,
    domainId: typeof data.domainId === 'string' && data.domainId ? data.domainId : null,
    domainLabel: typeof data.domainLabel === 'string' && data.domainLabel ? data.domainLabel : null,
    commentCount: data.commentCount ?? 0,
    reviewRound: data.reviewRound ?? 0,
    ...(isApprovedSubmission(data.approvedSubmission)
      ? { approvedSubmission: data.approvedSubmission }
      : {}),
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
    claimedAt: toDateOrNull(data.claimedAt),
    submittedAt: toDateOrNull(data.submittedAt),
    reviewedAt: toDateOrNull(data.reviewedAt),
  };
}

function isApprovedSubmission(value: unknown): value is ApprovedSubmissionLocation {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return typeof v.storagePathPrefix === 'string'
    && typeof v.uploadSessionId === 'string'
    && typeof v.uploadedByUid === 'string'
    && typeof v.uploadedAt === 'string'
    && typeof v.fileCount === 'number';
}

export function normalizeTaskTypeId(taskTypeId: unknown): string {
  return typeof taskTypeId === 'string' && taskTypeId.trim() ? taskTypeId : LEGACY_TYPE_ID;
}

function shuffle<T>(items: T[]): T[] {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export class ActiveTaskTypeClaimError extends Error {
  code = 'active-task-type-claim' as const;

  taskTypeId: string;

  domainId: string | null;

  activeTaskId: string | null;

  constructor(taskTypeId: string, activeTaskId: string | null, domainId: string | null = null) {
    super('You already have an active task of this type.');
    this.name = 'ActiveTaskTypeClaimError';
    this.taskTypeId = taskTypeId;
    this.domainId = domainId;
    this.activeTaskId = activeTaskId;
  }
}

export class FirstApprovalRequiredClaimError extends Error {
  code = 'first-approval-required-claim' as const;

  blockingTaskId: string | null;

  constructor(blockingTaskId: string | null = null) {
    super('Your first task in this campaign must be approved before you can claim another.');
    this.name = 'FirstApprovalRequiredClaimError';
    this.blockingTaskId = blockingTaskId;
  }
}

export class ClaimingPausedError extends Error {
  code = 'claiming-paused' as const;

  taskTypeId: string;

  domainId: string | null;

  constructor(taskTypeId: string, domainId: string | null = null) {
    super('Claiming is paused for this queue.');
    this.name = 'ClaimingPausedError';
    this.taskTypeId = taskTypeId;
    this.domainId = domainId;
  }
}

export class CampaignMaxTasksPerUserError extends Error {
  code = 'campaign-max-tasks-per-user' as const;

  campaignId: string;

  maxTasksPerUser: number;

  constructor(campaignId: string, maxTasksPerUser: number) {
    super('You have reached the maximum number of tasks for this campaign.');
    this.name = 'CampaignMaxTasksPerUserError';
    this.campaignId = campaignId;
    this.maxTasksPerUser = maxTasksPerUser;
  }
}

export class CampaignMaxClaimedTasksPerUserError extends Error {
  code = 'campaign-max-claimed-tasks-per-user' as const;

  campaignId: string;

  maxClaimedTasksPerUser: number;

  constructor(campaignId: string, maxClaimedTasksPerUser: number) {
    super('You have reached the maximum number of claimed tasks for this campaign.');
    this.name = 'CampaignMaxClaimedTasksPerUserError';
    this.campaignId = campaignId;
    this.maxClaimedTasksPerUser = maxClaimedTasksPerUser;
  }
}

function taskCommandDetails(error: TaskCommandError): Record<string, unknown> {
  return error.details;
}

// Command rejections that are normal user-flow outcomes rather than bugs.
// `runPageAction` still surfaces the message to the user, but skips
// `logGlobalError` so these don't group in Error Reporting. See the
// "What NOT to log" list in CLAUDE.md.
const EXPECTED_COMMAND_REJECTIONS = new Set([
  'ai-content-detected',
]);

/**
 * True when the error is a task-command rejection the user is expected to
 * hit and can act on themselves.
 */
export function isExpectedCommandRejection(error: unknown): boolean {
  return isTaskCommandError(error) && EXPECTED_COMMAND_REJECTIONS.has(error.commandCode);
}

/**
 * The AI-detection findings carried on a refused submit, shaped so the
 * verification rail can render them exactly like a Validate run's output.
 * Returns an empty array for any other error.
 *
 * The server already ran detection to make the refusal, so reusing its
 * findings avoids a second paid Pangram round trip for the same answer.
 */
export function aiDetectionComments(error: unknown): ValidatorComment[] {
  if (!isExpectedCommandRejection(error) || !isTaskCommandError(error)) return [];
  const raw = (error.details as Record<string, unknown>).comments;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const { fieldPath, severity, text } = entry as Record<string, unknown>;
    if (typeof fieldPath !== 'string' || !fieldPath) return [];
    if (typeof text !== 'string' || !text.trim()) return [];
    return [{
      fieldPath,
      severity: severity === 'warning' ? 'warning' : 'info',
      text,
    }];
  });
}

/** Preserve the queue's existing typed errors across the callable boundary. */
function mapTaskCommandError(error: unknown): unknown {
  if (!isTaskCommandError(error)) return error;
  const details = taskCommandDetails(error);
  const taskTypeId = typeof details.taskTypeId === 'string' ? details.taskTypeId : 'legacy';
  const domainId = typeof details.domainId === 'string' ? details.domainId : null;
  const taskId = typeof details.taskId === 'string' ? details.taskId : null;
  switch (error.commandCode) {
    case 'active-task-type-claim':
      return new ActiveTaskTypeClaimError(taskTypeId, taskId, domainId);
    case 'first-approval-required-claim':
      return new FirstApprovalRequiredClaimError(taskId);
    case 'claiming-paused':
      return new ClaimingPausedError(taskTypeId, domainId);
    case 'campaign-max-tasks-per-user':
      return new CampaignMaxTasksPerUserError(
        typeof details.campaignId === 'string' ? details.campaignId : '',
        typeof details.maxTasksPerUser === 'number' ? details.maxTasksPerUser : 0,
      );
    case 'campaign-max-claimed-tasks-per-user':
      return new CampaignMaxClaimedTasksPerUserError(
        typeof details.campaignId === 'string' ? details.campaignId : '',
        typeof details.maxClaimedTasksPerUser === 'number' ? details.maxClaimedTasksPerUser : 0,
      );
    default:
      return error;
  }
}

export function isActiveTaskTypeClaimError(value: unknown): value is ActiveTaskTypeClaimError {
  return value instanceof ActiveTaskTypeClaimError
    || (
      typeof value === 'object'
      && value !== null
      && ((value as { code?: string }).code === 'active-task-type-claim'
        || (value as { name?: string }).name === 'ActiveTaskTypeClaimError')
    );
}

export function isFirstApprovalRequiredClaimError(value: unknown): value is FirstApprovalRequiredClaimError {
  return value instanceof FirstApprovalRequiredClaimError
    || (
      typeof value === 'object'
      && value !== null
      && ((value as { code?: string }).code === 'first-approval-required-claim'
        || (value as { name?: string }).name === 'FirstApprovalRequiredClaimError')
    );
}

export function isClaimingPausedError(value: unknown): value is ClaimingPausedError {
  return value instanceof ClaimingPausedError
    || (
      typeof value === 'object'
      && value !== null
      && ((value as { code?: string }).code === 'claiming-paused'
        || (value as { name?: string }).name === 'ClaimingPausedError')
    );
}

export function isCampaignMaxTasksPerUserError(value: unknown): value is CampaignMaxTasksPerUserError {
  return value instanceof CampaignMaxTasksPerUserError
    || (
      typeof value === 'object'
      && value !== null
      && ((value as { code?: string }).code === 'campaign-max-tasks-per-user'
        || (value as { name?: string }).name === 'CampaignMaxTasksPerUserError')
    );
}

export function isCampaignMaxClaimedTasksPerUserError(
  value: unknown,
): value is CampaignMaxClaimedTasksPerUserError {
  return value instanceof CampaignMaxClaimedTasksPerUserError
    || (
      typeof value === 'object'
      && value !== null
      && ((value as { code?: string }).code === 'campaign-max-claimed-tasks-per-user'
        || (value as { name?: string }).name === 'CampaignMaxClaimedTasksPerUserError')
    );
}

/**
 * Live stream of every `in_review` task in a campaign — both unclaimed (the
 * "Awaiting review" pool) and reviewer-claimed. Consumers split the two via
 * `reviewerId === null`. Capped at 200 docs, ordered by recency; the
 * unclaimed pool stays small in practice because reviewers pick tasks up.
 *
 * Replaces the old broad `subscribeToQueue` for the review bucket. The other
 * queue buckets (myActive, myReviews, changesRequested) come from
 * `subscribeToMyOpenTasks` + `subscribeToMyReviews`, which are owner/reviewer-
 * scoped and naturally low-cardinality. The "available" pool is no longer
 * streamed — the page uses `countAvailableTasksByType` instead so the badge
 * count stays accurate even when the pool exceeds the streaming cap.
 */
export function subscribeToAwaitingReviewInCampaign(
  campaignId: string,
  onChange: (tasks: Task[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, TASKS),
    where('campaignId', '==', campaignId),
    where('status', '==', 'in_review'),
    orderBy('updatedAt', 'desc'),
    limit(200),
  );
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => mapTask(d.id, d.data()))),
    wrapSubscriptionError('tasks.subscribeToAwaitingReviewInCampaign', onError),
  );
}

/**
 * Live stream of in_review tasks the caller is reviewing, across all
 * campaigns. The viewmodel filters to the selected campaign in memory. Kept
 * cross-campaign for symmetry with `subscribeToMyOpenTasks` and so a future
 * cross-campaign reviews view can reuse it without a second query.
 */
export function subscribeToMyReviews(
  uid: string,
  onChange: (tasks: Task[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, TASKS),
    where('reviewerId', '==', uid),
    where('status', '==', 'in_review'),
    orderBy('updatedAt', 'desc'),
    limit(200),
  );
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => mapTask(d.id, d.data()))),
    wrapSubscriptionError('tasks.subscribeToMyReviews', onError),
  );
}

/**
 * Per-task-type "available" count for a campaign, via Firestore count()
 * aggregates. One server query per task type — runs in parallel. Used for
 * the "Claim next task" badge instead of streaming the available pool, which
 * could exceed the 200-doc subscription cap on big batches.
 *
 * Refresh on: campaign select, after a claim attempt, after `abandonTask`.
 * Admin-side bulk creates aren't observed live; the user sees the new count
 * the next time one of those events fires (or when they switch campaigns).
 */
export async function countAvailableTasksByType(
  campaignId: string,
  scopes: Array<string | TaskClaimScope>,
): Promise<Map<string, number>> {
  const result = new Map<string, number>();
  if (scopes.length === 0) {
    const snap = await getCountFromServer(
      query(
        collection(db, TASKS),
        where('campaignId', '==', campaignId),
        where('status', '==', 'available'),
      ),
    );
    result.set('', snap.data().count);
    return result;
  }
  const normalizedScopes = scopes.map((scope) => (
    typeof scope === 'string' ? { taskTypeId: scope, domainId: null } : scope
  ));
  const counts = await Promise.all(
    normalizedScopes.map((scope) =>
      getCountFromServer(
        query(
          collection(db, TASKS),
          where('campaignId', '==', campaignId),
          where('status', '==', 'available'),
          where('taskType', '==', scope.taskTypeId),
          ...(scope.domainId ? [where('domainId', '==', scope.domainId)] : []),
        ),
      ).then((snap) => [claimScopeKey(scope.taskTypeId, scope.domainId), snap.data().count] as const),
    ),
  );
  for (const [id, n] of counts) result.set(id, n);
  return result;
}

export async function countUserTasksInCampaign(
  campaignId: string,
  uid: string,
): Promise<number> {
  if (!campaignId || !uid) return 0;
  const snap = await getCountFromServer(query(
    collection(db, TASKS),
    where('campaignId', '==', campaignId),
    where('ownerId', '==', uid),
  ));
  return snap.data().count;
}

/**
 * Live list of tasks the caller still has open work on — anything they own
 * that isn't approved/archived, across every campaign. Used by the queue so a
 * former-active expert (or a user whose campaign has been deprecated) keeps
 * visibility of their in-flight work even though the campaign would otherwise
 * fall out of their dropdown.
 */
export function subscribeToMyOpenTasks(
  uid: string,
  onChange: (tasks: Task[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, TASKS),
    where('ownerId', '==', uid),
    where('status', 'in', ['claimed', 'in_progress', 'in_review', 'changes_requested']),
    orderBy('updatedAt', 'desc'),
    limit(200),
  );
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => mapTask(d.id, d.data()))),
    wrapSubscriptionError('tasks.subscribeToMyOpenTasks', onError),
  );
}

/**
 * Live list of the caller's approved tasks across every campaign. Split from
 * `subscribeToMyOpenTasks` because that helper deliberately excludes the
 * terminal `approved` status — most queue surfaces don't want it. The
 * "Submitted" tab on the queue page does, since it lets the owner reopen
 * approved work for rework.
 */
export function subscribeToMyApprovedTasks(
  uid: string,
  onChange: (tasks: Task[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, TASKS),
    where('ownerId', '==', uid),
    where('status', '==', 'approved'),
    orderBy('updatedAt', 'desc'),
    limit(200),
  );
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => mapTask(d.id, d.data()))),
    wrapSubscriptionError('tasks.subscribeToMyApprovedTasks', onError),
  );
}

/**
 * Live list of every approved task in one campaign — regardless of owner.
 * Used by the queue's reviewer/admin "Approved" tab so they can see what's
 * been signed off in the campaign. The viewmodel gates this subscription
 * by role (reviewer or admin only) so we don't burn reads for writers who
 * aren't supposed to see other people's approved work.
 *
 * Uses the existing `(campaignId ASC, status ASC, updatedAt DESC)` composite
 * index — already in firestore.indexes.json, no new index required.
 */
export function subscribeToApprovedTasksInCampaign(
  campaignId: string,
  onChange: (tasks: Task[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, TASKS),
    where('campaignId', '==', campaignId),
    where('status', '==', 'approved'),
    orderBy('updatedAt', 'desc'),
    limit(200),
  );
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => mapTask(d.id, d.data()))),
    wrapSubscriptionError('tasks.subscribeToApprovedTasksInCampaign', onError),
  );
}

export async function listAllTasksOnce(): Promise<Task[]> {
  const snap = await getDocs(query(collection(db, TASKS), orderBy('updatedAt', 'desc'), limit(200)));
  return snap.docs.map((d) => mapTask(d.id, d.data()));
}

// Statuses campaign/owner searches in Browse Tasks expose — anything that's been
// picked up by an owner, whether still in flight or already approved.
// `available` is excluded (no owner, nothing to browse yet) and
// `archived` is excluded (admin-soft-deleted, not meant to resurface).
export const BROWSABLE_TASK_STATUSES: TaskStatus[] = [
  'claimed',
  'in_progress',
  'in_review',
  'changes_requested',
  'approved',
];

export interface BrowseTasksQuery {
  /** Direct doc lookup. When set, other filters are ignored. */
  taskId?: string;
  /** Equality filter on `campaignId`. */
  campaignId?: string;
  /** Equality filter on `taskType`. */
  taskTypeId?: string;
  /** Optional equality filter on a browseable task status. */
  status?: TaskStatus;
  /** Equality filter on `ownerId`. */
  ownerId?: string;
}

/**
 * Admin-only one-shot query used by the Browse Tasks tool. Campaign/owner
 * searches use `BROWSABLE_TASK_STATUSES`; exact IDs return any existing task,
 * including tasks returned to the available pool after a stale claim.
 *
 * Query strategy: the Firestore indexes that already exist are
 * `(ownerId, status, updatedAt)` and `(campaignId, status, updatedAt)`.
 * We pick the more selective primary filter (ownerId when present), push
 * the status constraint and order-by into the server query, and apply the
 * remaining facets (taskType and campaignId when ownerId won the race)
 * in memory. Without a primary filter the query would require a new
 * `(taskType, status, updatedAt)` index or a full scan — the viewmodel
 * rejects that upstream by requiring at least one of taskId/ownerId/
 * campaignId before calling this.
 */
export async function fetchTasksForBrowse(
  filter: BrowseTasksQuery,
): Promise<Task[]> {
  if (filter.taskId && filter.taskId.trim()) {
    const snap = await getDoc(taskRef(filter.taskId.trim()));
    if (!snap.exists()) return [];
    return [mapTask(snap.id, snap.data())];
  }

  if (!filter.ownerId && !filter.campaignId) {
    throw new Error(
      'A campaign, owner, or task ID is required to browse tasks.',
    );
  }

  // Keep the browse tool bounded to the statuses it is designed to expose,
  // including when a deep link supplies an unsupported status value.
  if (filter.status && !BROWSABLE_TASK_STATUSES.includes(filter.status)) {
    return [];
  }

  const constraints = [];
  if (filter.ownerId) {
    constraints.push(where('ownerId', '==', filter.ownerId));
  } else if (filter.campaignId) {
    constraints.push(where('campaignId', '==', filter.campaignId));
  }
  constraints.push(
    filter.status
      ? where('status', '==', filter.status)
      : where('status', 'in', BROWSABLE_TASK_STATUSES),
  );
  constraints.push(orderBy('updatedAt', 'desc'));
  constraints.push(limit(500));

  const snap = await getDocs(query(collection(db, TASKS), ...constraints));
  let tasks = snap.docs.map((d) => mapTask(d.id, d.data()));

  // Secondary filters applied in memory because Firestore can't stack
  // more equality filters alongside an `in` clause without a wider index.
  if (filter.ownerId && filter.campaignId) {
    tasks = tasks.filter((t) => t.campaignId === filter.campaignId);
  }
  if (filter.taskTypeId) {
    tasks = tasks.filter((t) => t.taskType === filter.taskTypeId);
  }
  return tasks;
}

/**
 * Every task for a campaign, regardless of status. Used by the admin export
 * flow — callers should expect the full pool (up to `targetCount`, plus any
 * approved/archived history).
 */
export async function listTasksForCampaign(campaignId: string): Promise<Task[]> {
  const snap = await getDocs(
    query(
      collection(db, TASKS),
      where('campaignId', '==', campaignId),
      orderBy('createdAt', 'asc'),
    ),
  );
  return snap.docs.map((d) => mapTask(d.id, d.data()));
}

export function taskRef(taskId: string) {
  return doc(db, TASKS, taskId);
}

function userDisplayName(user: FirebaseUser): string {
  return user.displayName ?? user.email ?? 'Unknown';
}

/**
 * One-shot read from the server (bypassing the cache). The live `subscribeToTask`
 * stream is usually authoritative, but when a reviewer just claimed a task we
 * want to be certain the local state reflects the very latest submission — not
 * a snapshot captured before the worker's most recent re-upload. Returns `null`
 * when the doc is missing.
 */
export async function fetchLatestTaskFromServer(taskId: string): Promise<Task | null> {
  const snap = await getDocFromServer(taskRef(taskId));
  if (!snap.exists()) return null;
  return loadTaskFileContents(mapTask(snap.id, snap.data()));
}

export function subscribeToTask(
  taskId: string,
  onChange: (task: Task | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const load = createHoneydewTaskLoader();
  let revision = 0;
  const reportError = wrapSubscriptionError('tasks.subscribeToTask', onError, { taskId });
  const reportResultLoadError = wrapSubscriptionError(
    'tasks.subscribeToTask.resultLoad',
    undefined,
    { taskId },
  );
  const unsubscribe = onSnapshot(
    taskRef(taskId),
    (snap) => {
      const currentRevision = ++revision;
      if (!snap.exists()) {
        onChange(null);
        return;
      }
      const task = mapTask(snap.id, snap.data());
      if (task.taskType !== 'comp-workflows-scientific'
        || task.result?.contentStorage !== 'honeydew-files-v1') {
        onChange(task);
        return;
      }
      void load(task).then((loaded) => {
        if (currentRevision === revision) onChange(loaded);
      }).catch((error: Error) => {
        if (currentRevision !== revision) return;
        reportResultLoadError(error);
        onChange({
          ...task,
          resultLoadError: error.message,
        });
      });
    },
    reportError,
  );
  return () => {
    revision += 1;
    unsubscribe();
  };
}

/**
 * Writer-editable task title. Admins seed it at creation; the current owner
 * can rename it while they hold the task. Trims; rejects empty strings
 * because every task needs a label for queue lists and emails.
 * Rules allow only the current owner to call this while the task is in
 * claimed / in_progress / changes_requested.
 */
export async function updateTaskTitle(
  taskId: string,
  _user: FirebaseUser,
  title: string,
): Promise<void> {
  const trimmed = title.trim();
  if (!trimmed) throw new Error('Task title cannot be empty.');
  if (trimmed.length > 200) throw new Error('Task title cannot exceed 200 characters.');
  await updateDoc(taskRef(taskId), {
    title: trimmed,
    updatedAt: serverTimestamp(),
  });
}

export async function saveTaskDraft(
  taskId: string,
  user: FirebaseUser,
  result: Record<string, unknown>,
): Promise<void> {
  const persistedResult = compactTaskResult(result);
  assertResultFitsFirestore(persistedResult);
  await updateDoc(taskRef(taskId), {
    result: persistedResult,
    status: 'in_progress',
    ownerId: user.uid,
    ownerName: userDisplayName(user),
    ownerEmail: user.email ?? null,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Admin-only review edit path. Preserves the task's current review status,
 * owner, reviewer, and round while letting an admin fix the submitted result
 * in place during review.
 */
export async function saveReviewTaskResult(
  taskId: string,
  result: Record<string, unknown>,
): Promise<void> {
  const persistedResult = compactTaskResult(result);
  assertResultFitsFirestore(persistedResult);
  await updateDoc(taskRef(taskId), {
    result: persistedResult,
    updatedAt: serverTimestamp(),
  });
}

export async function submitTask(
  taskId: string,
  user: FirebaseUser,
  result: Record<string, unknown>,
  expectedReviewRound?: number,
  options: { acknowledgedAiDetection?: boolean } = {},
): Promise<void> {
  const submissionResult = normalizeOptionalGpuMetadata(compactTaskResult(result));
  assertResultFitsFirestore(submissionResult);
  try {
    await invokeTaskCommand('submit', {
      version: 1,
      taskId,
      result: submissionResult,
      ...(expectedReviewRound !== undefined ? { expectedReviewRound } : {}),
      // Set on a re-submit after AI detection flagged the first attempt.
      // The expert has seen the findings and is choosing to proceed.
      ...(options.acknowledgedAiDetection ? { acknowledgedAiDetection: true } : {}),
      idempotencyKey: newTaskCommandIdempotencyKey('submit', taskId),
    });
  } catch (error) {
    throw mapTaskCommandError(error);
  }
}

/**
 * Release a claimed/in-progress task back into the available pool, wiping
 * every field accumulated during this work/review cycle AND deleting all of
 * the task's comments, so the next claimant starts from a clean slate. This is
 * intentional per the product contract: "abandon" isn't "pause", it's "I'm
 * walking away; someone else can pick this up fresh." The Firestore rule
 * only allows this transition from `claimed`/`in_progress` (not during
 * review cycles).
 */
export async function abandonTask(taskId: string): Promise<void> {
  try {
    await invokeTaskCommand('abandon', {
      version: 1,
      taskId,
      idempotencyKey: newTaskCommandIdempotencyKey('abandon', taskId),
    });
  } catch (error) {
    throw mapTaskCommandError(error);
  }
}

export async function claimReview(
  taskId: string,
  _user: FirebaseUser,
  expectedReviewRound?: number,
): Promise<void> {
  try {
    await invokeTaskCommand('claimReview', {
      version: 1,
      taskId,
      ...(expectedReviewRound !== undefined ? { expectedReviewRound } : {}),
      idempotencyKey: newTaskCommandIdempotencyKey('claimReview', taskId),
    });
  } catch (error) {
    throw mapTaskCommandError(error);
  }
}

/**
 * Admin-only assignment of an in-review task to a reviewer configured for
 * the task's campaign. Eligibility and admin authorization are rechecked by
 * the versioned command before the task is updated.
 */
export async function adminAssignReview(
  taskId: string,
  reviewerId: string,
  expectedReviewRound?: number,
  expectedReviewerId?: string | null,
): Promise<void> {
  try {
    await invokeTaskCommand('assignReview', {
      version: 1,
      taskId,
      reviewerId,
      ...(expectedReviewRound !== undefined ? { expectedReviewRound } : {}),
      ...(expectedReviewerId !== undefined ? { expectedReviewerId } : {}),
      idempotencyKey: newTaskCommandIdempotencyKey('assignReview', taskId),
    });
  } catch (error) {
    throw mapTaskCommandError(error);
  }
}

/**
 * Reviewer-initiated unclaim — drops the reviewer pointer while leaving the
 * task at `in_review` so it lands back in the awaiting-review pool. Used when
 * a reviewer picks up a task but realises they can't (or shouldn't) finish it.
 * Round counter and submitted result are untouched; the next reviewer sees the
 * same submission the original reviewer did.
 */
export async function releaseReview(taskId: string, expectedReviewRound?: number, expectedReviewerId?: string | null): Promise<void> {
  try {
    await invokeTaskCommand('releaseReview', {
      ...(expectedReviewerId !== undefined ? { expectedReviewerId } : {}),
      version: 1,
      taskId,
      ...(expectedReviewRound !== undefined ? { expectedReviewRound } : {}),
      idempotencyKey: newTaskCommandIdempotencyKey('releaseReview', taskId),
    });
  } catch (error) {
    throw mapTaskCommandError(error);
  }
}

function campaignQueueClaimingPaused(
  campaignData: Record<string, unknown> | undefined,
  taskTypeId: string,
  domainId: string | null,
): boolean {
  return readCampaignClaimPolicy(campaignData, taskTypeId, domainId).claimingPaused;
}

function requiresFirstApprovalBeforeMoreClaims(
  campaignData: Record<string, unknown> | undefined,
): boolean {
  return readCampaignClaimPolicy(campaignData, '').firstApproval;
}

function readMaxTasksPerUserFromCampaign(
  campaignData: Record<string, unknown> | undefined,
): number | null {
  return readCampaignClaimPolicy(campaignData, '').maxTasks;
}

function readMaxClaimedTasksPerUserFromCampaign(
  campaignData: Record<string, unknown> | undefined,
): number | null {
  return readCampaignClaimPolicy(campaignData, '').maxClaimedTasks;
}

async function assertFirstApprovalGateAllowsClaim(
  campaignId: string,
  user: FirebaseUser,
  campaignData: Record<string, unknown> | undefined,
): Promise<void> {
  if (!requiresFirstApprovalBeforeMoreClaims(campaignData)) return;

  const approvedSnap = await getDocs(query(
    collection(db, TASKS),
    where('campaignId', '==', campaignId),
    where('ownerId', '==', user.uid),
    where('status', '==', 'approved'),
    limit(1),
  ));
  if (!approvedSnap.empty) return;

  const openSnap = await getDocs(query(
    collection(db, TASKS),
    where('campaignId', '==', campaignId),
    where('ownerId', '==', user.uid),
    where('status', 'in', ['claimed', 'in_progress', 'in_review', 'changes_requested']),
    limit(1),
  ));
  if (!openSnap.empty) {
    throw new FirstApprovalRequiredClaimError(openSnap.docs[0].id);
  }
}

async function assertCampaignMaxTasksPerUserAllowsClaim(
  campaignId: string,
  user: FirebaseUser,
  campaignData: Record<string, unknown> | undefined,
): Promise<void> {
  let maxTasksPerUser = readMaxTasksPerUserFromCampaign(campaignData);
  if (campaignData?.maxTasksPerTrustedUser != null) {
    const userSnap = await getDoc(doc(db, 'users', user.uid));
    const trustedIds = userSnap.data()?.trustedCampaignIds;
    maxTasksPerUser = readCampaignClaimPolicy(campaignData, '', null,
      Array.isArray(trustedIds) && trustedIds.includes(campaignId)).maxTasks;
  }
  if (maxTasksPerUser == null) return;

  const currentCount = await countUserTasksInCampaign(campaignId, user.uid);
  if (currentCount >= maxTasksPerUser) {
    throw new CampaignMaxTasksPerUserError(campaignId, maxTasksPerUser);
  }
}

async function assertCampaignMaxClaimedTasksPerUserAllowsClaim(
  campaignId: string,
  user: FirebaseUser,
  campaignData: Record<string, unknown> | undefined,
): Promise<void> {
  const maxClaimedTasksPerUser = readMaxClaimedTasksPerUserFromCampaign(campaignData);
  if (maxClaimedTasksPerUser == null) return;

  const snap = await getCountFromServer(query(
    collection(db, TASKS),
    where('campaignId', '==', campaignId),
    where('ownerId', '==', user.uid),
    where('status', 'in', ['claimed', 'in_progress']),
  ));
  const currentCount = snap.data().count;
  if (currentCount >= maxClaimedTasksPerUser) {
    throw new CampaignMaxClaimedTasksPerUserError(campaignId, maxClaimedTasksPerUser);
  }
}

async function claimAvailableTask(
  taskId: string,
  campaignId: string,
  options: { bypassClaimingPaused?: boolean } = {},
): Promise<boolean> {
  try {
    await invokeTaskCommand('claim', {
      version: 1,
      campaignId,
      taskId,
      idempotencyKey: newTaskCommandIdempotencyKey('claim', taskId),
      ...(options.bypassClaimingPaused ? { bypassClaimingPaused: true } : {}),
    });
    return true;
  } catch (err) {
    if (isTaskCommandError(err) && err.commandCode === 'task-not-available') {
      return false;
    }
    throw mapTaskCommandError(err);
  }
}

/**
 * Atomically claim the next available task in a campaign. Runs a Firestore
 * transaction on each candidate so concurrent callers can't both grab the
 * same task — if another user wins the race, we skip to the next candidate.
 * When `taskTypeId` is provided, limits the claim to that task type within
 * the campaign — used by the multi-type queue so experts can pick which
 * type to work on. Returns the claimed task id, or `null` when the pool is
 * empty.
 */
export async function claimNextTaskInCampaign(
  campaignId: string,
  user: FirebaseUser,
  expertId: string | null,
  taskTypeId?: string | null,
  domainId?: string | null,
  options: { bypassClaimingPaused?: boolean } = {},
): Promise<string | null> {
  const campaignSnap = await getDoc(doc(db, 'campaigns', campaignId));
  if (!options.bypassClaimingPaused && taskTypeId && campaignQueueClaimingPaused(
    campaignSnap.exists() ? campaignSnap.data() : undefined,
    taskTypeId,
    domainId ?? null,
  )) {
    throw new ClaimingPausedError(taskTypeId, domainId ?? null);
  }
  await assertFirstApprovalGateAllowsClaim(
    campaignId,
    user,
    campaignSnap.exists() ? campaignSnap.data() : undefined,
  );
  await assertCampaignMaxTasksPerUserAllowsClaim(
    campaignId,
    user,
    campaignSnap.exists() ? campaignSnap.data() : undefined,
  );
  await assertCampaignMaxClaimedTasksPerUserAllowsClaim(
    campaignId,
    user,
    campaignSnap.exists() ? campaignSnap.data() : undefined,
  );

  for (let pass = 0; pass < 3; pass++) {
    const constraints = [
      where('campaignId', '==', campaignId),
      where('status', '==', 'available'),
      ...(taskTypeId ? [where('taskType', '==', taskTypeId)] : []),
      ...(domainId ? [where('domainId', '==', domainId)] : []),
      limit(50),
    ];
    const snap = await getDocs(query(collection(db, TASKS), ...constraints));
    if (snap.empty) return null;
    for (const candidate of shuffle(snap.docs)) {
      const claimed = await claimAvailableTask(candidate.id, campaignId, options);
      if (claimed) {
        return candidate.id;
      }
    }
  }
  return null;
}

/**
 * Atomically claim the next unassigned review in a campaign. Same race-safe
 * pattern as `claimNextTaskInCampaign` — transaction per candidate, skip when
 * someone else picks it up first. When `taskTypeId` is provided, limits the
 * pickup to reviews of that task type. Returns the claimed task id, or
 * `null` when nothing is awaiting review.
 */
export async function claimNextReviewInCampaign(
  campaignId: string,
  user: FirebaseUser,
  taskTypeId?: string | null,
  domainId?: string | null,
): Promise<string | null> {
  for (let pass = 0; pass < 3; pass++) {
    const constraints = [
      where('campaignId', '==', campaignId),
      where('status', '==', 'in_review'),
      ...(taskTypeId ? [where('taskType', '==', taskTypeId)] : []),
      ...(domainId ? [where('domainId', '==', domainId)] : []),
      orderBy('updatedAt', 'asc'),
      limit(20),
    ];
    const snap = await getDocs(query(collection(db, TASKS), ...constraints));
    const candidates = snap.docs.filter((d) => !d.data().reviewerId);
    if (candidates.length === 0) return null;
    for (const candidate of candidates) {
      try {
        const data = candidate.data();
        await claimReview(
          candidate.id,
          user,
          Number.isInteger(data.reviewRound) ? data.reviewRound : 0,
        );
        return candidate.id;
      } catch (err) {
        if (isTaskCommandError(err)
          && ['reviewer-already-claimed', 'invalid-transition', 'stale-revision'].includes(err.commandCode)) {
          continue;
        }
        throw err;
      }
    }
  }
  return null;
}

/**
 * Admin tombstone — flips a task to the terminal `archived` state. Doc stays
 * around for forensics; `BROWSABLE_TASK_STATUSES` excludes archived so the
 * Browse Tasks tool stops surfacing it. Any per-type claim lock the previous
 * owner held becomes stale, but that's harmless: their next claim of the same
 * type updates the lock in the same transaction (the rule short-circuits when
 * the lock's old task is no longer claimed/in_progress).
 */
export async function archiveTask(taskId: string): Promise<void> {
  await updateDoc(taskRef(taskId), {
    status: 'archived',
    updatedAt: serverTimestamp(),
  });
}

/**
 * Admin-only escape hatch for Browse Tasks. Status-specific pointer cleanup
 * keeps "put it back in review" and similar repairs from leaving stale
 * reviewer assignments attached to the task.
 */
export async function adminSetTaskStatus(taskId: string, status: TaskStatus): Promise<void> {
  if (!TASK_STATUSES.includes(status)) {
    throw new Error(`Invalid task status: ${status}`);
  }
  await updateDoc(taskRef(taskId), {
    status,
    updatedAt: serverTimestamp(),
    // Status repair starts a new administrative lifecycle decision, so it
    // must not carry a hidden rework reservation into a later submission.
    reworkReviewerId: null,
    reworkReviewerName: null,
    ...(status === 'available'
      ? {
          ownerId: null,
          ownerName: null,
          ownerEmail: null,
          expertId: null,
          claimedAt: null,
          reviewerId: null,
          reviewerName: null,
        }
      : {}),
    ...(status === 'in_review' || status === 'changes_requested'
      ? {
          reviewerId: null,
          reviewerName: null,
        }
      : {}),
    ...(status === 'approved'
      ? {
          reviewedAt: serverTimestamp(),
        }
      : {}),
  });
}

export async function approveTask(
  taskId: string,
  approvedSubmission?: ApprovedSubmissionLocation,
  expectedReviewRound?: number,
): Promise<void> {
  try {
    await invokeTaskCommand('approve', {
      version: 1,
      taskId,
      ...(expectedReviewRound !== undefined ? { expectedReviewRound } : {}),
      ...(approvedSubmission ? { approvedSubmission } : {}),
      idempotencyKey: newTaskCommandIdempotencyKey('approve', taskId),
    });
  } catch (error) {
    throw mapTaskCommandError(error);
  }
}

export async function requestChanges(
  taskId: string,
  options: { comment?: string; expectedReviewRound?: number } = {},
): Promise<void> {
  try {
    await invokeTaskCommand('requestChanges', {
      version: 1,
      taskId,
      ...(options.comment ? { comment: options.comment } : {}),
      ...(options.expectedReviewRound !== undefined
        ? { expectedReviewRound: options.expectedReviewRound }
        : {}),
      idempotencyKey: newTaskCommandIdempotencyKey('requestChanges', taskId),
    });
  } catch (error) {
    throw mapTaskCommandError(error);
  }
}

export async function escalateReview(
  taskId: string,
  note: string,
  expectedReviewRound?: number,
): Promise<void> {
  try {
    await invokeTaskCommand('escalateReview', {
      version: 1,
      taskId,
      note: note.trim(),
      ...(expectedReviewRound !== undefined ? { expectedReviewRound } : {}),
      idempotencyKey: newTaskCommandIdempotencyKey('escalateReview', taskId),
    });
  } catch (error) {
    throw mapTaskCommandError(error);
  }
}

/**
 * Owner-driven reopen of an approved task. Flips back to `changes_requested`
 * so it lands in the rework queue and the existing `changes_requested →
 * in_progress` re-claim path can carry it forward. Reviewer pointer is
 * cleared so this owner-initiated rework picks up a fresh reviewer on next
 * submission; reviewer-requested rework is separately reserved for the same
 * reviewer by the server commands.
 * `reviewedAt` is intentionally left untouched as a record of when the prior
 * approval happened.
 */
export async function reworkApprovedTask(
  taskId: string,
  expectedReviewRound?: number,
): Promise<void> {
  try {
    await invokeTaskCommand('reopen', {
      version: 1,
      taskId,
      ...(expectedReviewRound !== undefined ? { expectedReviewRound } : {}),
      idempotencyKey: newTaskCommandIdempotencyKey('reopen', taskId),
    });
  } catch (error) {
    throw mapTaskCommandError(error);
  }
}

/**
 * Owner-driven pullback of a submitted task before a reviewer claims it.
 * Moves the task from the awaiting-review pool back into the owner's active
 * queue with the submitted result preserved as the editable draft.
 */
export async function returnSubmittedTaskToQueue(
  taskId: string,
  user: FirebaseUser,
  expectedReviewRound?: number,
): Promise<void> {
  try {
    await invokeTaskCommand('return', {
      version: 1,
      taskId,
      ...(expectedReviewRound !== undefined ? { expectedReviewRound } : {}),
      idempotencyKey: newTaskCommandIdempotencyKey('return', taskId),
    });
  } catch (error) {
    throw mapTaskCommandError(error);
  }
}

export interface CampaignStatusCounts {
  total: number;
  available: number;
  /** `claimed`. Tasks held by a worker but not opened into active work yet. */
  claimed: number;
  /** `in_progress`. Tasks the worker has opened/started. */
  inProgress: number;
  /** `changes_requested`. Reviewer kicked it back; worker has it again to fix. */
  rework: number;
  inReview: number;
  approved: number;
  /** Admin-tombstoned tasks. Terminal; excluded from the progress bar. */
  archived: number;
}

/**
 * Per-status task counts for a single campaign. `claimed` and `inProgress`
 * are separate so admins can distinguish untouched holds from active work.
 * `rework` is `changes_requested` only — split out so the progress bar can
 * show rework as its own segment. Pass `taskTypeId` to scope counts to a
 * single task type within the campaign (used by per-type progress bars on
 * multi-type campaigns).
 */
/**
 * Available (unclaimed) tasks in a single campaign/task-type/domain scope.
 * Used by the admin "assign task" modal to let an admin pick an existing
 * available task to hand to an expert. Equality-only filters — no composite
 * index beyond the one `countTasksByStatus` already relies on. Capped at 200
 * (the modal is a picker, not a full browser).
 */
export async function listAvailableTasksForScope(
  campaignId: string,
  taskTypeId: string,
  domainId: string | null,
): Promise<Task[]> {
  const snap = await getDocs(
    query(
      collection(db, TASKS),
      where('campaignId', '==', campaignId),
      where('taskType', '==', taskTypeId),
      ...(domainId ? [where('domainId', '==', domainId)] : []),
      where('status', '==', 'available'),
      limit(200),
    ),
  );
  const tasks = snap.docs.map((d) => mapTask(d.id, d.data()));
  // Drop tasks that carry a domain when the caller asked for the unscoped
  // pool, so a null-domain assignment can't accidentally grab a domained task.
  const scoped = domainId ? tasks : tasks.filter((t) => !t.domainId);
  return scoped.sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true }));
}

export async function countTasksByStatus(
  campaignId: string,
  taskTypeId?: string | null,
  domainId?: string | null,
): Promise<CampaignStatusCounts> {
  const base = taskTypeId
    ? query(
        collection(db, TASKS),
        where('campaignId', '==', campaignId),
        where('taskType', '==', taskTypeId),
        ...(domainId ? [where('domainId', '==', domainId)] : []),
      )
    : query(collection(db, TASKS), where('campaignId', '==', campaignId));
  const [totalSnap, availableSnap, claimedSnap, inProgressSnap, reworkSnap, inReviewSnap, approvedSnap, archivedSnap] =
    await Promise.all([
      getCountFromServer(base),
      getCountFromServer(query(base, where('status', '==', 'available'))),
      getCountFromServer(query(base, where('status', '==', 'claimed'))),
      getCountFromServer(query(base, where('status', '==', 'in_progress'))),
      getCountFromServer(query(base, where('status', '==', 'changes_requested'))),
      getCountFromServer(query(base, where('status', '==', 'in_review'))),
      getCountFromServer(query(base, where('status', '==', 'approved'))),
      getCountFromServer(query(base, where('status', '==', 'archived'))),
    ]);
  return {
    total: totalSnap.data().count,
    available: availableSnap.data().count,
    claimed: claimedSnap.data().count,
    inProgress: inProgressSnap.data().count,
    rework: reworkSnap.data().count,
    inReview: inReviewSnap.data().count,
    approved: approvedSnap.data().count,
    archived: archivedSnap.data().count,
  };
}
